import { Controller } from "@hotwired/stimulus";
import moment from "moment";
import * as echarts from "echarts";
import * as Turbo from "@hotwired/turbo";

export default class extends Controller {
  static targets = ["chartBody"];
  static values = {
    url: String,
  };

  connect() {
    this.chart = null;
    this.responseTimeData = null;
    this.initializeChart();
  }

  initializeChart() {
    if (!this.hasChartBodyTarget) {
      return;
    }

    this.chart = echarts.init(this.chartBodyTarget);
    this.chart.setOption({
      tooltip: {
        trigger: "axis",
        axisPointer: {
          label: {
            formatter: function (params) {
              return moment(params.value).format("dddd, MMM D, h:mm a");
            },
          },
        },
      },
      toolbox: {
        showTitle: false,
        tooltip: {
          show: true,
          formatter: (params) => {
            return params.title;
          },
        },
        feature: {
          brush: {
            type: ["lineX"],
            title: {
              lineX: "Select range",
            },
          },
        },
      },
      brush: {
        throttleType: "debounce",
        throttleDelay: 500,
        outOfBrush: {
          colorAlpha: 0.0,
        },
      },
      xAxis: {
        type: "time",
        min: "dataMin",
        max: "dataMax",
      },
      yAxis: {
        type: "value",
      },
      dataZoom: [
        {
          start: 0,
          end: 100,
        },
      ],
      series: [
        {
          name: "Placeholder Bars",
          type: "bar",
          color: ["rgba(256, 256, 256, 0.0)"],
          emphasis: { disabled: true },
          select: { disabled: true },
          blur: {
            itemStyle: {
              color: "rgba(256, 256, 256, 0.0)",
            },
          },
          tooltip: {
            show: false,
          },
        },
        {
          name: "Response Time",
          type: "line",
          color: ["#00A9A5"],
        },
      ],
      grid: {
        containLabel: true,
        left: "0",
        right: "15",
        top: "25",
        bottom: "50",
      },
    });

    // When a user uses the range selection tool.
    this.chart.on("brushSelected", (params) => {
      let seriesComponent = params.batch[0].selected[0];

      if (seriesComponent) {
        let minIndex = seriesComponent.dataIndex.at(0);
        let maxIndex = seriesComponent.dataIndex.at(-1);

        // If the selection is too small, the min and max index might be the
        // same. In this case we go to the next index.
        if (minIndex == maxIndex) {
          maxIndex = minIndex + 1;
        }

        let minValue = this.responseTimeData[minIndex];
        let maxValue = this.responseTimeData[maxIndex];

        if (minValue && maxValue) {
          let min = Math.round(minValue[0]);
          let max = null;

          // If there is no max value, we set to the current time.
          if (maxValue) {
            max = Math.round(maxValue[0]);
          } else {
            max = Date.now();
          }

          if (min && max) {
            this.selection(min, max);
          }
        }
      }
    });
  }

  selection(min, max) {
    const query = {
      start: min,
      stop: max,
    };

    Turbo.visit(`${this.urlValue}?${$.param(query)}`, {
      frame: "site-response-times",
    });
  }

  updateData(e) {
    this.responseTimeData = e.detail.data;
    this.redrawChart();
    this.resizeChart();
  }

  redrawChart() {
    if (!this.chart) {
      return;
    }

    this.chart.setOption({
      series: [
        {
          name: "Response Time",
          symbol: this.responseTimeData.length > 1 ? "none" : "dot",
          data: this.responseTimeData,
        },
        { name: "Placeholder Bars", data: this.responseTimeData },
      ],
    });

    this.chart.dispatchAction({
      type: "brush",
      command: "clear",
      areas: [],
    });
  }

  resizeChart() {
    if (!this.chart) {
      return;
    }

    this.chart.resize();
  }
}
